import { useState, useEffect } from 'react';
import { TrendingUp, Clock, Hash, ToggleLeft, ToggleRight, AlertCircle, Pencil, X, Check } from 'lucide-react';
import { api } from '../api/client';

const TYPE_COLORS = {
  SCALPING:  'text-amber-400 bg-amber-400/10 border-amber-400/20',
  INTRADAY:  'text-blue-400 bg-blue-400/10 border-blue-400/20',
  SWING:     'text-purple-400 bg-purple-400/10 border-purple-400/20',
  CUSTOM:    'text-slate-400 bg-slate-400/10 border-slate-400/20',
};

const SYMBOLS   = ['XAUUSD', 'EURUSD', 'USDMXN', 'GBPUSD', 'USDJPY'];
const TYPES     = ['SCALPING', 'INTRADAY', 'SWING', 'CUSTOM'];
const INTERVALS = [
  { label: '5 min',  value: 300  },
  { label: '15 min', value: 900  },
  { label: '30 min', value: 1800 },
  { label: '1 hora', value: 3600 },
  { label: '4 horas',value: 14400},
];

// ─── Modal de edición ─────────────────────────────────────────────────────────
function EditModal({ strategy, onClose, onSaved }) {
	const [form, setForm] = useState({
	    intervalSeconds:        strategy.intervalSeconds || 3600,
	    symbol:                 strategy.symbol || '',
	    type:                   strategy.type || 'CUSTOM',
	    ownerNote:              strategy.ownerNote || '',
	    stopLossAtrMult:        strategy.stopLossAtrMult || 1.5,
	    takeProfitRr:           strategy.takeProfitRr || 2.0,
	    maxConcurrentPositions: strategy.maxConcurrentPositions || 1,
	    allowedHoursCdmx:       strategy.allowedHoursCdmx || [],
	    allowedSessions:        strategy.allowedSessions || [],
	});
  const [saving, setSaving]   = useState(false);
  const [error,  setError]    = useState(null);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.updateStrategy(strategy.id, form);
      onSaved();
      onClose();
    } catch (e) {
      setError('Error al guardar. Revisa los logs.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
   <div className="bg-[#13131A] border border-[#2A2A3E] rounded-lg w-full max-w-md p-6 space-y-5 max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="font-mono font-bold text-slate-100 text-sm">Editar — {strategy.name}</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300 transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Intervalo */}
        <div className="space-y-1.5">
          <label className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">Intervalo de ciclo</label>
          <div className="flex flex-wrap gap-2">
            {INTERVALS.map(opt => (
              <button
                key={opt.value}
                onClick={() => set('intervalSeconds', opt.value)}
                className={`px-3 py-1.5 rounded text-[11px] font-mono border transition-colors ${
                  form.intervalSeconds === opt.value
                    ? 'border-amber-400 bg-amber-400/10 text-amber-400'
                    : 'border-[#2A2A3E] text-slate-500 hover:border-slate-500'
                }`}
              >
                {opt.label}
              </button>
            ))}
            {/* Input manual si no está en los presets */}
            <input
              type="number"
              min={60}
              value={form.intervalSeconds}
              onChange={e => set('intervalSeconds', parseInt(e.target.value) || 3600)}
              className="w-24 px-2 py-1.5 rounded text-[11px] font-mono border border-[#2A2A3E] bg-[#0A0A0F] text-slate-300 focus:border-amber-400 focus:outline-none"
              placeholder="seg"
            />
          </div>
        </div>

        {/* Símbolo */}
        <div className="space-y-1.5">
          <label className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">Símbolo</label>
          <div className="flex flex-wrap gap-2">
            {SYMBOLS.map(sym => (
              <button
                key={sym}
                onClick={() => set('symbol', sym)}
                className={`px-3 py-1.5 rounded text-[11px] font-mono border transition-colors ${
                  form.symbol === sym
                    ? 'border-amber-400 bg-amber-400/10 text-amber-400'
                    : 'border-[#2A2A3E] text-slate-500 hover:border-slate-500'
                }`}
              >
                {sym}
              </button>
            ))}
          </div>
        </div>

        {/* Tipo */}
        <div className="space-y-1.5">
          <label className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">Tipo</label>
          <div className="flex flex-wrap gap-2">
            {TYPES.map(t => (
              <button
                key={t}
                onClick={() => set('type', t)}
                className={`px-3 py-1.5 rounded text-[11px] font-mono border transition-colors ${
                  form.type === t
                    ? 'border-amber-400 bg-amber-400/10 text-amber-400'
                    : 'border-[#2A2A3E] text-slate-500 hover:border-slate-500'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* Owner note */}
        <div className="space-y-1.5">
          <label className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">Nota</label>
          <textarea
            rows={3}
            value={form.ownerNote}
            onChange={e => set('ownerNote', e.target.value)}
            placeholder="Notas del operador..."
            className="w-full px-3 py-2 rounded text-[11px] font-mono border border-[#2A2A3E] bg-[#0A0A0F] text-slate-300 focus:border-amber-400 focus:outline-none resize-none"
          />
        </div>

        {error && (
          <div className="flex items-center gap-2 text-red-400 text-[11px] font-mono border border-red-400/20 bg-red-400/5 px-3 py-2 rounded">
            <AlertCircle size={11} />
            {error}
          </div>
        )}
		
		{/* Parámetros avanzados */}
		<details className="group">
		  <summary className="text-[11px] font-mono text-slate-500 uppercase tracking-wider cursor-pointer hover:text-amber-400 transition-colors select-none">
		    ▶ Parámetros de riesgo
		  </summary>

		  <div className="mt-3 space-y-4 pl-2 border-l border-[#2A2A3E]">

		    {/* SL y TP */}
		    <div className="grid grid-cols-2 gap-3">
		      <div className="space-y-1.5">
		        <label className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">
		          SL Mult (ATR)
		        </label>
		        <input
		          type="number" step="0.1" min="0.5" max="5.0"
		          value={form.stopLossAtrMult}
		          onChange={e => set('stopLossAtrMult', parseFloat(e.target.value) || 1.5)}
		          className="w-full px-2 py-1.5 rounded text-[11px] font-mono border border-[#2A2A3E] bg-[#0A0A0F] text-slate-300 focus:border-amber-400 focus:outline-none"
		        />
		        <p className="text-[10px] font-mono text-slate-600">Rango: 0.5 – 5.0</p>
		      </div>
		      <div className="space-y-1.5">
		        <label className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">
		          TP R:R
		        </label>
		        <input
		          type="number" step="0.1" min="1.0" max="5.0"
		          value={form.takeProfitRr}
		          onChange={e => set('takeProfitRr', parseFloat(e.target.value) || 2.0)}
		          className="w-full px-2 py-1.5 rounded text-[11px] font-mono border border-[#2A2A3E] bg-[#0A0A0F] text-slate-300 focus:border-amber-400 focus:outline-none"
		        />
		        <p className="text-[10px] font-mono text-slate-600">Rango: 1.0 – 5.0</p>
		      </div>
		    </div>

		    {/* Max posiciones */}
		    <div className="space-y-1.5">
		      <label className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">
		        Max posiciones simultáneas
		      </label>
		      <div className="flex gap-2">
		        {[1, 2, 3].map(n => (
		          <button
		            key={n}
		            onClick={() => set('maxConcurrentPositions', n)}
		            className={`px-4 py-1.5 rounded text-[11px] font-mono border transition-colors ${
		              form.maxConcurrentPositions === n
		                ? 'border-amber-400 bg-amber-400/10 text-amber-400'
		                : 'border-[#2A2A3E] text-slate-500 hover:border-slate-500'
		            }`}
		          >
		            {n}
		          </button>
		        ))}
		      </div>
		    </div>

		    {/* Sesiones */}
		    <div className="space-y-1.5">
		      <label className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">
		        Sesiones permitidas
		      </label>
		      <div className="flex gap-2">
		        {['London', 'NY', 'Asian'].map(sess => {
		          const active = form.allowedSessions.includes(sess);
		          return (
		            <button
		              key={sess}
		              onClick={() => set('allowedSessions', active
		                ? form.allowedSessions.filter(s => s !== sess)
		                : [...form.allowedSessions, sess]
		              )}
		              className={`px-3 py-1.5 rounded text-[11px] font-mono border transition-colors ${
		                active
		                  ? 'border-amber-400 bg-amber-400/10 text-amber-400'
		                  : 'border-[#2A2A3E] text-slate-500 hover:border-slate-500'
		              }`}
		            >
		              {sess}
		            </button>
		          );
		        })}
		      </div>
		    </div>

		    {/* Horas CDMX */}
		    <div className="space-y-1.5">
		      <label className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">
		        Horas permitidas (CDMX)
		      </label>
		      <div className="flex flex-wrap gap-1.5">
		        {Array.from({length: 24}, (_, i) => i).map(h => {
		          const active = form.allowedHoursCdmx.includes(h);
		          return (
		            <button
		              key={h}
		              onClick={() => set('allowedHoursCdmx', active
		                ? form.allowedHoursCdmx.filter(x => x !== h)
		                : [...form.allowedHoursCdmx, h].sort((a,b) => a-b)
		              )}
		              className={`w-8 py-1 rounded text-[10px] font-mono border transition-colors ${
		                active
		                  ? 'border-amber-400 bg-amber-400/10 text-amber-400'
		                  : 'border-[#2A2A3E] text-slate-600 hover:border-slate-500'
		              }`}
		            >
		              {h}
		            </button>
		          );
		        })}
		      </div>
		      <p className="text-[10px] font-mono text-slate-600">
		        {form.allowedHoursCdmx.length === 0 ? 'Sin restricción de horario' : `${form.allowedHoursCdmx.length} horas seleccionadas`}
		      </p>
		    </div>

		  </div>
		</details>

        {/* Botones */}
        <div className="flex gap-3 pt-1">
          <button
            onClick={onClose}
            className="flex-1 py-2 rounded text-[11px] font-mono border border-[#2A2A3E] text-slate-500 hover:text-slate-300 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-2 rounded text-[11px] font-mono bg-amber-500 hover:bg-amber-400 text-black font-bold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? 'Guardando...' : <><Check size={12} /> Guardar</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Card de estrategia ───────────────────────────────────────────────────────
function StrategyCard({ strategy, onToggle, onEdit }) {
  const [toggling, setToggling] = useState(false);

  const handleToggle = async () => {
    setToggling(true);
    try { await onToggle(strategy.id); }
    finally { setToggling(false); }
  };

  const typeColor = TYPE_COLORS[strategy.type] || TYPE_COLORS.CUSTOM;

  return (
    <div className={`bg-[#13131A] border rounded p-5 transition-all duration-200 ${
      strategy.active ? 'border-[#1E1E2E]' : 'border-[#1E1E2E] opacity-50'
    }`}>

      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-2 h-2 rounded-full mt-1 ${strategy.active ? 'bg-green-400 animate-pulse' : 'bg-slate-600'}`} />
          <div>
            <h3 className="font-mono font-bold text-slate-100 text-sm">{strategy.name}</h3>
            {strategy.description && (
              <p className="text-[11px] text-slate-500 font-mono mt-0.5">{strategy.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onEdit(strategy)}
            className="text-slate-600 hover:text-amber-400 transition-colors"
          >
            <Pencil size={14} />
          </button>
          <button
            onClick={handleToggle}
            disabled={toggling}
            className="text-slate-400 hover:text-amber-400 transition-colors disabled:opacity-50"
          >
            {strategy.active
              ? <ToggleRight size={24} className="text-green-400" />
              : <ToggleLeft size={24} />
            }
          </button>
        </div>
      </div>

      {/* Pills */}
      <div className="flex flex-wrap gap-2 mb-4">
        {strategy.type && (
          <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${typeColor}`}>
            {strategy.type}
          </span>
        )}
        {strategy.symbol && (
          <span className="text-[10px] font-mono px-2 py-0.5 rounded border text-amber-400 bg-amber-400/10 border-amber-400/20">
            {strategy.symbol}
          </span>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="flex items-center gap-2">
          <Clock size={12} className="text-slate-600" />
          <span className="text-[11px] font-mono text-slate-400">
            Ciclo: <span className="text-slate-200">{strategy.intervalSeconds || 3600}s</span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Hash size={12} className="text-slate-600" />
          <span className="text-[11px] font-mono text-slate-400">
            Magic: <span className="text-slate-200">{strategy.magicNumber || '—'}</span>
          </span>
        </div>
        {strategy.maxConcurrentPositions && (
          <div className="flex items-center gap-2">
            <TrendingUp size={12} className="text-slate-600" />
            <span className="text-[11px] font-mono text-slate-400">
              Max pos: <span className="text-slate-200">{strategy.maxConcurrentPositions}</span>
            </span>
          </div>
        )}
      </div>

      {/* Owner note */}
      {strategy.ownerNote && (
        <div className="mt-4 pt-3 border-t border-[#1E1E2E] flex items-start gap-2">
          <AlertCircle size={11} className="text-slate-600 mt-0.5 shrink-0" />
          <p className="text-[11px] font-mono text-slate-500">{strategy.ownerNote}</p>
        </div>
      )}
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────
export default function Strategies() {
  const [strategies, setStrategies] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [editing,    setEditing]    = useState(null); // strategy siendo editada

  const loadStrategies = () =>
    api.getStrategies()
      .then(r => setStrategies(r.data))
      .catch(() => setError('No se pudieron cargar las estrategias'))
      .finally(() => setLoading(false));

  useEffect(() => { loadStrategies(); }, []);

  const handleToggle = async (id) => {
    await api.toggleStrategy(id);
    await loadStrategies();
  };

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="flex items-center gap-3 text-amber-500 font-mono text-sm">
        <div className="w-2 h-2 rounded-full bg-amber-500 animate-ping" />
        Cargando estrategias...
      </div>
    </div>
  );

  const active   = strategies.filter(s => s.active);
  const inactive = strategies.filter(s => !s.active);

  return (
    <div className="p-8 space-y-8">

      {editing && (
        <EditModal
          strategy={editing}
          onClose={() => setEditing(null)}
          onSaved={loadStrategies}
        />
      )}

      {/* Header */}
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-100 tracking-tight">Estrategias</h1>
        <p className="text-xs text-slate-500 font-mono mt-1">
          {active.length} activas · {inactive.length} inactivas
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-400 text-xs font-mono border border-red-400/20 bg-red-400/5 px-3 py-2 rounded">
          <AlertCircle size={12} />
          {error}
        </div>
      )}

      {/* Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {strategies.map(s => (
          <StrategyCard
            key={s.id}
            strategy={s}
            onToggle={handleToggle}
            onEdit={setEditing}
          />
        ))}
      </div>

    </div>
  );
}